import { Component, computed, inject, OnInit, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UiBadgeComponent, UiEmptyStateComponent, UiLoadingComponent } from '@design-system';
import { firstValueFrom } from 'rxjs';
import { WalletService } from '../../../../services/wallet.service';
import { WalletTransferRowsStore } from '../../data-access/wallet-transfer-rows.store';
import { WalletTransferDetailBottomSheetComponent } from '../../transfer-history/wallet-transfer-detail-bottom-sheet/wallet-transfer-detail-bottom-sheet.component';
import { TransferHistoryItem } from '../../transfer-history/wallet-transfer-history.model';
import { transferStateKey } from '../../transfer-history/wallet-transfer-history.utils';
import { WalletTransferRowComponent } from '../../transfer-history/wallet-transfer-row/wallet-transfer-row.component';

/**
 * «تراکنش‌های اخیر» on the wallet tab: the transfers `latest-active` names, which the server
 * picks and sends whole — there is no next page to read. The full, paged history is
 * `WalletTransferHistoryComponent`, at `/wallet/transfer-history`.
 */
@Component({
	selector: 'app-last-active-transfers',
	imports: [
		UiBadgeComponent,
		UiEmptyStateComponent,
		UiLoadingComponent,
		WalletTransferRowComponent,
		WalletTransferDetailBottomSheetComponent
	],
	templateUrl: './last-active-transfers.component.html',
	providers: [WalletTransferRowsStore],
	// Fills the wallet page's leftover height only while it shows rows; the loading, empty and
	// error states keep their own height instead of stretching the card down the page.
	host: { class: 'block min-h-0', '[class.flex-1]': 'showsRows()' }
})
export class LastActiveTransfersComponent implements OnInit {
	private readonly walletService = inject(WalletService);
	private readonly rows = inject(WalletTransferRowsStore);

	readonly requests = this.rows.requests;
	readonly selectedRequest = this.rows.selectedRequest;
	/** The rows scrolled — carries their scroller, for the wallet page to read where it stands. */
	readonly scrolled = output<HTMLElement>();
	readonly loading = signal(true);
	readonly error = signal<string | null>(null);

	/**
	 * The header badge on «تراکنش‌های اخیر»: how many of the listed requests are still on
	 * their way. Zero hides the badge.
	 */
	readonly pendingCount = computed(
		() => this.requests().filter(request => transferStateKey(request.state) === 'pending').length
	);

	/** The rows are on screen — not the loading, error or empty state. */
	readonly showsRows = computed(() => !this.loading() && !this.error() && this.requests().length > 0);

	private readonly detailSheet = viewChild<WalletTransferDetailBottomSheetComponent>('detailSheet');

	constructor() {
		// Re-read in place rather than waiting for a revisit.
		this.rows.stale$.pipe(takeUntilDestroyed()).subscribe(() => this.reload());
	}

	ngOnInit(): void {
		this.load();
	}

	load(): void {
		this.loading.set(true);
		this.error.set(null);
		this.read()
			.catch(() => this.error.set('خطایی رخ داد!'))
			.finally(() => this.loading.set(false));
	}

	/**
	 * A notification says the list is stale, not that it is gone: the rows on screen stay
	 * while the new ones are read, and a read that fails leaves them there rather than
	 * trading what the trader is reading for an error.
	 */
	reload(): void {
		this.read()
			.then(() => this.rows.reloadSelectedEvents())
			.catch(() => undefined);
	}

	async openDetails(request: TransferHistoryItem): Promise<void> {
		this.rows.select(request);
		await this.rows.loadEvents(request);
		this.detailSheet()?.open();
	}

	onDetailsClosed(): void {
		this.rows.clearSelection();
	}

	private async read(): Promise<void> {
		this.rows.setRows(await firstValueFrom(this.walletService.getLatestActiveTransferRequests()), false);
	}
}
